import React from 'react';
import { PieChart } from 'components';
import { formatters, buildPredeterminedReturnSeries } from 'services';
import { REAL_TIME_SCENARIO } from 'state';
import type { Studio2Player, Studio2Session } from '../../live/types';
import KpiTile from '../KpiTile';
import MiniChart from '../MiniChart';
import ReturnVsBenchmarkChart from '../ReturnVsBenchmarkChart';
import {
  pct,
  pct2,
  money,
  num,
  signClass,
  signedMoney,
  BAND_COLORS,
} from '../../helpers';

/* Stable asset-class-group → color map so a class (e.g. "Equities") is the same
 * color in BOTH the long and short pies. Names match GROUP_ASSET_NAME; unknown
 * labels fall back to a deterministic palette by their position in the combined
 * label set, so the two pies still agree. */
const ALLOC_CLASS_COLORS: Record<string, string> = {
  Equities: '#0f5fd6',
  Bonds: '#0e9f6e',
  'HTM Bonds': '#0b7285',
  Alternatives: '#d6336c',
  FX: '#d48806',
  Crypto: '#7048e8',
  Cash: '#868e96',
  Repo: '#1098ad',
  CDS: '#e8590c',
  Futures: '#5f3dc4',
};
const ALLOC_FALLBACK_PALETTE = [
  '#13b8a6',
  '#f59f00',
  '#15803d',
  '#9c36b5',
  '#ae3ec9',
  '#0ca678',
];

/** Build a label→color map shared across both books. Known classes get their
 *  fixed color; anything else is assigned deterministically from the fallback
 *  palette by sorted position in the combined label set. */
const buildClassColorMap = (labels: string[]): Record<string, string> => {
  const map: Record<string, string> = {};
  let fallbackIdx = 0;
  [...new Set(labels)].sort().forEach(label => {
    if (ALLOC_CLASS_COLORS[label]) {
      map[label] = ALLOC_CLASS_COLORS[label];
    } else {
      map[label] =
        ALLOC_FALLBACK_PALETTE[fallbackIdx % ALLOC_FALLBACK_PALETTE.length];
      fallbackIdx += 1;
    }
  });
  return map;
};

const positiveAllocation = (value: number | undefined) =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;

const toAllocSlices = (alloc: Record<string, number>) =>
  Object.entries(alloc)
    .filter(([, value]) => positiveAllocation(value) > 0)
    .map(([label, value]) => ({ label, value }));

/** One book (Long/Short): a caption above a bare pie. The built-in ApexCharts
 *  legend is hidden — both pies share a single asset-class legend rendered once
 *  beside them (see studio2.css `.studio2__alloc-*`). */
const AllocPie: React.FC<{
  label: string;
  data: { label: string; value: number }[];
  colors: string[];
}> = ({ label, data, colors }) => (
  <div className="studio2__alloc-pie">
    <div className="studio2__alloc-book-title">{label}</div>
    <div className="studio2__alloc-book-pie">
      <PieChart
        data={data}
        colors={colors}
        height="100%"
        showLegend={false}
        style={{ height: '100%' }}
      />
    </div>
  </div>
);

const SnapshotPanel: React.FC<{
  player: Studio2Player;
  // Cohort size for the Rank KPI. Omitted in the per-player (self) view, where
  // rank is dropped — the Rank tile then renders nothing.
  cohortSize?: number;
  session: Studio2Session;
}> = ({ player, cohortSize, session }) => {
  const m = player.last_metrics;
  const r = player.report.returns;
  // Gross P&L bridge — cohort-wide values from the analysis flow (all players),
  // replacing the legacy individual-report gross_profit/gross_loss (which were
  // realized-only and focused-player-only).
  const g = player.gross_pnl;

  // Cumulative player return vs benchmark over the session — same series the v1
  // PnL-evolution tab plots (player_history / metrics.market_index_return).
  // Historical sessions index the x-axis by day number; real-time by timestamp.
  const isRealTime = session.scenario_type === REAL_TIME_SCENARIO;
  const isPredetermined =
    session.time_advancement_type === 'predetermined_intervals';
  const freq = session.frequency;
  let returnSeries = player.player_history.map((h, i) => ({
    x: isRealTime ? h.timestamp : i * freq + 1,
    y: h.portfolio_return,
  }));
  // With no benchmark configured (any kind) `market_index_return` is a flat 0
  // seed, so don't feed a benchmark series — the tile becomes "Return evolution"
  // and plots the player line alone.
  const hasBenchmark = session.has_benchmark;
  let benchmarkSeries = hasBenchmark
    ? player.metrics.map((row, i) => ({
        x: isRealTime ? row.timestamp : i * freq + 1,
        y: row.market_index_return,
      }))
    : undefined;
  if (isPredetermined) {
    // THIRD branch: knit canonical anchors + display fills. Trainer surface plots absolute
    // datetimes; trainee stays on relative "Day N" (no calendar leak). Fixed/RT above untouched.
    const knitted = buildPredeterminedReturnSeries(
      player,
      session,
      session.display_surface,
    );
    returnSeries = knitted.portfolio;
    benchmarkSeries = hasBenchmark ? knitted.benchmark : undefined;
  }
  const snapshotDateTimeX =
    isRealTime || (isPredetermined && session.display_surface === 'trainer');

  // Long / short books — each pie normalised within its own book; same class
  // gets the same color across both via the shared color map.
  const longData = toAllocSlices(player.allocations_long);
  const shortData = toAllocSlices(player.allocations_short);
  const classColors = buildClassColorMap([
    ...longData.map(d => d.label),
    ...shortData.map(d => d.label),
  ]);
  const longColors = longData.map(d => classColors[d.label]);
  const shortColors = shortData.map(d => classColors[d.label]);
  // Shared asset-class legend for BOTH pies (union, sorted; same color map).
  const legendItems = Object.keys(classColors).sort();

  // Long / short tornado bar — absolute $ per asset-class group, so bar lengths
  // convey real book magnitude (which the within-book-normalised pies flatten).
  // Short is plotted negative so it extends LEFT of zero; the bar is `stacked`
  // → one continuous diverging bar per class split at zero. Keep even small
  // positive groups: hiding them would make a diversified book look all-cash.
  const longAbs = player.allocations_long_abs;
  const shortAbs = player.allocations_short_abs;
  const barGroups = [
    ...new Set([...Object.keys(longAbs), ...Object.keys(shortAbs)]),
  ]
    .filter(
      g =>
        positiveAllocation(longAbs[g]) > 0 ||
        positiveAllocation(shortAbs[g]) > 0,
    )
    .sort();
  const barLong = barGroups.map(g => positiveAllocation(longAbs[g]));
  const barShort = barGroups.map(g => -positiveAllocation(shortAbs[g]));
  const absMoney = (v: number) => money(Math.abs(v));

  const compCats = ['Realized', 'Unrealized', 'FX'];
  const compVals = [r.realized_return, r.unrealized_return, r.fx_return];
  const compColors = compVals.map(v =>
    v >= 0 ? BAND_COLORS[0] : BAND_COLORS[3],
  );

  return (
    <div className="studio2__snapshot">
      {/* KPI row */}
      <div className="studio2__tiles studio2__tiles--kpi">
        <KpiTile label="Turnover" value={money(player.total_turnover)} />
        <KpiTile
          label="Total PnL"
          value={signedMoney(player.last_pnl_sum)}
          valueClass={signClass(player.last_pnl_sum)}
        />
        <KpiTile
          label="Return"
          value={pct(player.last_portfolio_return)}
          valueClass={signClass(player.last_portfolio_return)}
        />
        <KpiTile
          label="FX return"
          value={pct(player.last_fx_return)}
          valueClass={signClass(player.last_fx_return)}
        />
        <KpiTile
          label="Realized"
          value={pct(r.realized_return)}
          valueClass={signClass(r.realized_return)}
        />
        <KpiTile
          label="Unrealized"
          value={pct(r.unrealized_return)}
          valueClass={signClass(r.unrealized_return)}
        />
        {cohortSize != null && (
          <KpiTile label="Rank" value={`#${r.rank}`} sub={`of ${cohortSize}`} />
        )}
      </div>

      {/* Risk row */}
      <div className="studio2__tiles studio2__tiles--risk">
        <KpiTile
          label="Volatility"
          value={pct(m.portfolio_volatility)}
          tooltip="Annualised portfolio volatility"
        />
        <KpiTile
          label="Alpha"
          value={pct2(m.alpha)}
          valueClass={signClass(m.alpha)}
        />
        <KpiTile label="Beta" value={num(m.beta)} />
        <KpiTile
          label="Sharpe"
          value={num(m.sharpe_ratio)}
          valueClass={signClass(m.sharpe_ratio)}
        />
        <KpiTile
          label="Treynor"
          value={num(m.treynor_ratio)}
          valueClass={signClass(m.treynor_ratio)}
        />
        <KpiTile
          label="Max DD"
          value={pct(m.mdd)}
          valueClass="s2-neg"
          tooltip="Maximum drawdown"
        />
        <KpiTile
          label="VaR (5%)"
          value={money(m.var05)}
          valueClass="s2-neg"
          tooltip="Value at Risk, 5%"
        />
      </div>

      {/* Charts row */}
      <div className="studio2__snapshot-charts">
        <div className="studio2__card">
          <div className="studio2__card-head">
            <span
              className="studio2__card-title"
              title="Book values and posted margin for leveraged positions. Spot FX trade notionals are excluded."
            >
              Capital allocation
            </span>
          </div>
          <div className="studio2__card-body studio2__card-body--pad0">
            <div className="studio2__alloc-layout">
              {/* Top: Long/Short tornado bar (absolute $ per asset class). */}
              <div className="studio2__alloc-bar">
                <MiniChart
                  type="bar"
                  horizontal
                  stacked
                  series={[
                    { name: 'Long', data: barLong },
                    { name: 'Short', data: barShort },
                  ]}
                  categories={barGroups}
                  colors={[BAND_COLORS[0], BAND_COLORS[3]]}
                  valueFormatter={absMoney}
                  height="100%"
                />
              </div>
              {/* Bottom: the two within-book pies side by side, sharing one
                  asset-class legend on the right. */}
              <div className="studio2__alloc-pies">
                <AllocPie
                  label={`Long: ${money(player.allocations_long_total)}`}
                  data={longData}
                  colors={longColors}
                />
                <AllocPie
                  label={`Short: ${money(player.allocations_short_total)}`}
                  data={shortData}
                  colors={shortColors}
                />
                <ul className="studio2__alloc-legend-shared">
                  {legendItems.map(label => (
                    <li key={label}>
                      <span
                        className="studio2__alloc-legend-dot"
                        style={{ background: classColors[label] }}
                      />
                      {label}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="studio2__card">
          <div className="studio2__card-head">
            <span className="studio2__card-title">Return composition</span>
          </div>
          <div className="studio2__card-body studio2__card-body--pad0">
            <MiniChart
              type="bar"
              series={[{ name: 'Return', data: compVals }]}
              categories={compCats}
              colors={compColors}
              distributed
              valueFormatter={formatters.percent1Digit}
              height="100%"
            />
          </div>
        </div>

        {/* Column 3: P&L bridge stats stacked above the return-vs-benchmark
            chart, each in its own tile-card. */}
        <div className="studio2__snapshot-col">
          <div className="studio2__card studio2__card--shrink">
            <div className="studio2__card-head">
              <span className="studio2__card-title">P&amp;L bridge</span>
            </div>
            <div className="studio2__card-body">
              <div className="studio2__statline">
                <span>Gross profit</span>
                <strong className="s2-pos">
                  {signedMoney(g.gross_profit)}
                </strong>
              </div>
              <div className="studio2__statline">
                <span>Gross loss</span>
                <strong className="s2-neg">{signedMoney(g.gross_loss)}</strong>
              </div>
              <div className="studio2__statline">
                <span>Total net return</span>
                <strong className={signClass(g.total_net_return)}>
                  {signedMoney(g.total_net_return)}
                </strong>
              </div>
              <div className="studio2__statline">
                <span>Benchmark return</span>
                <strong className={signClass(m.market_index_return)}>
                  {pct(m.market_index_return)}
                </strong>
              </div>
            </div>
          </div>

          <div className="studio2__card studio2__card--grow">
            <div className="studio2__card-head">
              <span className="studio2__card-title">
                {hasBenchmark ? 'Return vs benchmark' : 'Return evolution'}
              </span>
            </div>
            <div className="studio2__card-body studio2__card-body--pad0">
              <ReturnVsBenchmarkChart
                playerName={player.name}
                playerData={returnSeries}
                benchmarkData={benchmarkSeries}
                valueFormatter={formatters.percent1Digit}
                isDateTimeX={snapshotDateTimeX}
                height="100%"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SnapshotPanel;
